/**
 * DashboardView Component
 * Main dashboard display with grid of graph components
 */

import React, { useState } from "react";
import {
  Box,
  Grid,
  Paper,
  Typography,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useAnalyticsDashboardWithData } from "@/api/hooks/analytics/useAnalytics";
import { useUpdateDashboard } from "@/api/hooks/analytics/useAnalytics";
import { useUpdateScopeFilters } from "@/api/hooks/analytics/useAnalytics";
import { useAddGraph } from "@/api/hooks/analytics/useAnalytics";
import { useUpdateGraph as useUpdateGraphMutation } from "@/api/hooks/analytics/useAnalytics";
import { useDeleteGraph } from "@/api/hooks/analytics/useAnalytics";
import { useScopeFilterOptions } from "@/api/hooks/analytics/useAnalytics";
import { useDuplicateDashboard } from "@/api/hooks/analytics/useAnalytics";
import { useQueryClient } from "@tanstack/react-query";
import type {

  AnalyticsGraphDto,
  AnalyticsGraphUpdateDto,
} from "@/api/types/dtos/analytics/AnalyticsDto";
import { DashboardCreator, DashboardSaveData } from "./DashboardCreator";
import { BarChart } from "./charts/BarChart";
import { PieChart } from "./charts/PieChart";
import { SumDisplay } from "./charts/SumDisplay";
import { CalendarHeatmap } from "./charts/CalendarHeatmap";
import { MapChart } from "./charts/MapChart";
import { FarmListModal } from "./FarmListModal";
import { GraphEditorModal } from "./GraphEditorModal";

interface DashboardViewProps {
  dashboardId: string;
  onBack?: () => void;
  onDeleteDashboard: (dashboardId: string) => void;
  canDelete?: boolean;
  canEdit?: boolean;
}

export const DashboardView: React.FC<DashboardViewProps> = ({
  dashboardId,
  onBack,
  onDeleteDashboard,
  canDelete = false,
  canEdit = false,
}) => {
  const { dashboard, graphData, isLoading, error, refetch } =
    useAnalyticsDashboardWithData(dashboardId);
  const queryClient = useQueryClient();
  const updateDashboardMutation = useUpdateDashboard();
  const updateScopeFiltersMutation = useUpdateScopeFilters();
  const addGraphMutation = useAddGraph();
  const updateGraphMutation = useUpdateGraphMutation();
  const deleteGraphMutation = useDeleteGraph();
  const duplicateDashboardMutation = useDuplicateDashboard();

  // Fetch scope filter options for each filter type
  const groupOptions = useScopeFilterOptions("GROUPS");
  const cityOptions = useScopeFilterOptions("CITIES");
  const regionOptions = useScopeFilterOptions("REGIONS");
  const cropOptions = useScopeFilterOptions("CROPS");

  // Combine all options into a map for quick lookup
  const filterLabelMap = React.useMemo(() => {
    const map = new Map<string, string>();
    [groupOptions.data, cityOptions.data, regionOptions.data, cropOptions.data].forEach(
      (options) => {
        options?.forEach((opt) => map.set(opt.value, opt.label));
      }
    );
    return map;
  }, [groupOptions.data, cityOptions.data, regionOptions.data, cropOptions.data]);

  // Helper to get label for a filter value (UUID -> label)
  const getFilterLabel = (_filterType: string, filterValue: string): string => {
    try {
      const values: string[] = JSON.parse(filterValue);
      const labels = values.map((v) => filterLabelMap.get(v) || v);
      return labels.join(", ");
    } catch {
      return filterValue;
    }
  };

  const [drillDownState, setDrillDownState] = useState<{
    graphId: string;
    filterValue: string;
    graphType: string;
  } | null>(null);

  const [isEditing, setIsEditing] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editingGraph, setEditingGraph] = useState<AnalyticsGraphDto | null>(null);

  const handleGraphClick = (graphId: string, filterValue: string, graphType: string) => {
    setDrillDownState({ graphId, filterValue, graphType });
  };

  const handleCloseDrillDown = () => {
    setDrillDownState(null);
  };

  // Helper functions for responsive grid sizing
  const getGridWidthMd = (graphType: string): number => {
    switch (graphType) {
      case "SUM":
        return 6;
      case "BAR":
      case "CALENDAR_HEATMAP":
      case "MAP":
        return 12;
      case "PIE":
      default:
        return 6;
    }
  };

  const getGridWidthLg = (graphType: string): number => {
    switch (graphType) {
      case "SUM":
        return 4;
      case "PIE":
        return 6;
      case "MAP":
        return 12;
      default:
        return 12;
    }
  };

  const getGridWidthXl = (graphType: string): number => {
    switch (graphType) {
      case "SUM":
        return 3;
      case "PIE":
        return 4;
      case "MAP":
        return 12;
      default:
        return 12;
    }
  };

  const handleDuplicateDashboard = async (dashboardId: string) => {
    await duplicateDashboardMutation.mutateAsync(dashboardId);
    setIsEditing(false);
    queryClient.invalidateQueries({ queryKey: ["analytics"] });
  };

  const handleConfirmDelete = () => {
    if (!dashboard) return;
    onDeleteDashboard(dashboard.id);
    setDeleteDialogOpen(false);
  };

  const handleSaveDashboard = async (data: DashboardSaveData) => {
    if (!dashboard) return;

    // Debug: log the data being saved
    console.log("[DashboardView] Saving dashboard:", {
      name: data.name,
      scopeFilters: data.scopeFilters,
    });

    // Update dashboard metadata
    await updateDashboardMutation.mutateAsync({
      dashboardId: dashboard.id,
      dto: {
        name: data.name,
        description: data.description,
      },
    });

    // Update scope filters if they changed
    await updateScopeFiltersMutation.mutateAsync({
      dashboardId: dashboard.id,
      scopeFilters: data.scopeFilters,
    });

    // Handle graphs
    const existingGraphIds = new Set(dashboard.graphs.map((g) => g.id));

    // Add or update graphs
    for (const graph of data.graphs) {
      const graphId = graph.id;
      if (graphId && graphId.startsWith("temp-")) {
        // New graph - add it
        const { id, ...graphData } = graph;
        await addGraphMutation.mutateAsync({
          dashboardId: dashboard.id,
          dto: graphData,
        });
      } else if (graphId && existingGraphIds.has(graphId)) {
        // Existing graph - update it
        const { id, ...graphData } = graph;
        await updateGraphMutation.mutateAsync({
          dashboardId: dashboard.id,
          graphId,
          dto: graphData,
        });
      } else if (!graphId) {
        // Graph without ID (shouldn't happen, but handle it)
        await addGraphMutation.mutateAsync({
          dashboardId: dashboard.id,
          dto: graph,
        });
      }
    }

    // Delete graphs that were removed
    for (const existingGraph of dashboard.graphs) {
      const stillExists = data.graphs.some((g) => g.id === existingGraph.id);
      if (!stillExists) {
        await deleteGraphMutation.mutateAsync({
          dashboardId: dashboard.id,
          graphId: existingGraph.id,
        });
      }
    }

    queryClient.invalidateQueries({ queryKey: ["analytics"] });
    setIsEditing(false);
    refetch();
  };

  const handleSaveGraph = async (graphData: AnalyticsGraphUpdateDto) => {
    if (!dashboard || !editingGraph) return;

    await updateGraphMutation.mutateAsync({
      dashboardId: dashboard.id,
      graphId: editingGraph.id,
      dto: graphData,
    });

    queryClient.invalidateQueries({ queryKey: ["analytics"] });
    setEditingGraph(null);
    refetch();
  };

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
        <Typography>Loading dashboard data...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
        <Typography color="error">Error loading dashboard: {(error as Error).message}</Typography>
        <Button onClick={() => refetch()} sx={{ ml: 2 }}>
          Retry
        </Button>
      </Box>
    );
  }

  if (!dashboard || !graphData || !dashboard.graphs || dashboard.graphs.length === 0) {
    return (
      <Box sx={{ p: 4 }}>
        <Typography>No data available</Typography>
      </Box>
    );
  }

  if (graphData.length === 0) {
    return (
      <Box sx={{ p: 4 }}>
        <Typography>No graphs configured for this dashboard</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          {onBack && (
            <IconButton onClick={onBack} size="small">
              <ArrowBackIcon />
            </IconButton>
          )}
          <Typography
            variant="h3"
            sx={{
              fontWeight: 700,
              color: "var(--text-primary)",
            }}
          >
            {dashboard.name}
          </Typography>
        </Box>
        {(canEdit || canDelete) && (
          <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
            {canEdit && (
              <Button
                variant="outlined"
                startIcon={<EditIcon />}
                onClick={() => setIsEditing(true)}
                sx={{
                  fontWeight: 500,
                  borderRadius: "var(--button-border-radius)",
                  borderColor: "var(--border-color)",
                  color: "var(--text-primary)",
                  "&:hover": {
                    backgroundColor: "var(--tertiary)",
                    borderColor: "var(--primary)",
                  },
                }}
              >
                Editar
              </Button>
            )}
            {canDelete && (
              <Button
                variant="outlined"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={() => setDeleteDialogOpen(true)}
                sx={{
                  fontWeight: 500,
                  borderRadius: "var(--button-border-radius)",
                }}
              >
                Eliminar
              </Button>
            )}
          </Box>
        )}
      </Box>

      {dashboard.description && (
        <Typography variant="body2" sx={{ mb: 3, color: "var(--text-secondary)" }}>
          {dashboard.description}
        </Typography>
      )}

      {/* Scope Filters Display */}
      {dashboard.scopeFilters && dashboard.scopeFilters.length > 0 && (
        <Box sx={{ mb: 3, display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center" }}>
          <Typography variant="body2" sx={{ color: "var(--text-primary)" }}>
            Filtros Generales:
          </Typography>
          {dashboard.scopeFilters.map((filter) => (
            <Typography
              key={filter.id}
              variant="caption"
              sx={{
                color: "var(--text-tertiary)",
                backgroundColor: "#AFBEFF",
                fontWeight: 700,
                width: "fit-content",
                alignSelf: "center",
                padding: "4px 10px",
                borderRadius: "12px",
                textAlign: "center",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                lineHeight: 1,
              }}
            >
              {filter.filterType}: {getFilterLabel(filter.filterType, filter.filterValue)}
            </Typography>
          ))}
        </Box>
      )}

      {/* Graphs Grid */}
      <Grid container spacing={3}>
        {graphData
          .sort((a, b) => {
            const graphA = dashboard.graphs.find((g) => g.id === a.graphId);
            const graphB = dashboard.graphs.find((g) => g.id === b.graphId);
            return (graphA?.positionIndex ?? 0) - (graphB?.positionIndex ?? 0);
          })
          .map((graphResponse) => {
            const graph = dashboard.graphs.find((g) => g.id === graphResponse.graphId);
            if (!graph) {
              return null;
            }
            return (
              <Grid
                item
                xs={12}
                md={getGridWidthMd(graph.graphType)}
                lg={getGridWidthLg(graph.graphType)}
                xl={getGridWidthXl(graph.graphType)}
                key={graphResponse.graphId}
              >
                <GraphCard
                  graph={graph}
                  graphData={graphResponse.data}
                  onGraphClick={handleGraphClick}
                  onEdit={(g) => setEditingGraph(g)}
                  canEdit={canEdit}
                />
              </Grid>
            );
          })}
      </Grid>

      {/* Drill-down Dialog */}
      {drillDownState && (
        <FarmListModal
          open={!!drillDownState}
          graphId={drillDownState.graphId}
          filterValue={drillDownState.filterValue}
          onClose={handleCloseDrillDown}
        />
      )}

      {/* Edit Dashboard Modal */}
      <DashboardCreator
        open={isEditing}
        onClose={() => setIsEditing(false)}
        onSave={handleSaveDashboard}
        onDuplicate={handleDuplicateDashboard}
        dashboard={dashboard}
        isLoading={updateDashboardMutation.isPending}
      />

      {/* Edit Graph Modal */}
      {editingGraph && (
        <GraphEditorModal
          open={!!editingGraph}
          onClose={() => setEditingGraph(null)}
          onSave={handleSaveGraph}
          graph={editingGraph}
          isLoading={updateGraphMutation.isPending}
        />
      )}

      {/* Delete Dashboard Confirmation */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Eliminar dashboard</DialogTitle>
        <DialogContent>
          <DialogContentText>
            ¿Estás seguro de que deseas eliminar &quot;{dashboard?.name}&quot;? Esta acción no se
            puede deshacer.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancelar</Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained">
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

// Graph Card Component
interface GraphCardProps {
  graph: AnalyticsGraphDto;
  graphData: any;
  onGraphClick: (graphId: string, filterValue: string, graphType: string) => void;
  onEdit: (graph: AnalyticsGraphDto) => void;
  canEdit?: boolean;
}

const GraphCard: React.FC<GraphCardProps> = ({ graph, graphData, onGraphClick, onEdit, canEdit = false }) => {
  // Get the appropriate height for the chart type
  const getChartHeight = () => {
    switch (graph.graphType) {
      case "SUM":
        return 180;
      case "BAR":
      case "PIE":
      case "CALENDAR_HEATMAP":
        return 320;
      case "MAP":
        return 500;
      default:
        return 320;
    }
  };

  // Safety check for graphData
  if (!graphData) {
    return (
      <Paper
        sx={{
          height: "100%",
          p: 2,
          display: "flex",
          flexDirection: "column",
          borderRadius: "var(--card-border-radius)",
          border: "1px solid var(--border-color)",
          boxShadow: "0 1px 3px rgba(0, 0, 0, 0.04)",
        }}
      >
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "var(--text-primary)" }}>
            {graph.name}
          </Typography>
        </Box>
        <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Typography sx={{ color: "var(--text-secondary)" }}>No data available</Typography>
        </Box>
      </Paper>
    );
  }

  const renderGraph = () => {
    switch (graph.graphType) {
      case "BAR":
        return (
          <BarChart data={graphData} onClick={(value) => onGraphClick(graph.id, value, "BAR")} />
        );
      case "PIE":
        return (
          <PieChart data={graphData} onClick={(value) => onGraphClick(graph.id, value, "PIE")} />
        );
      case "SUM":
        return <SumDisplay data={graphData} />;
      case "CALENDAR_HEATMAP":
        return (
          <CalendarHeatmap
            data={graphData}
            onClick={(date) => onGraphClick(graph.id, date, "CALENDAR_HEATMAP")}
          />
        );
      case "MAP":
        return <MapChart data={graphData} />;
      default:
        return <Typography color="error">Unknown graph type</Typography>;
    }
  };

  return (
    <Paper
      sx={{
        height: "100%",
        p: 2,
        display: "flex",
        flexDirection: "column",
        borderRadius: "var(--card-border-radius)",
        border: "1px solid var(--border-color)",
        boxShadow: "0 1px 3px rgba(0, 0, 0, 0.04)",
        transition: "box-shadow 150ms ease",
        "&:hover": {
          boxShadow: "0 2px 8px rgba(11, 15, 40, 0.08)",
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 1,
          flexShrink: 0,
        }}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "var(--text-primary)" }}>
          {graph.name}
        </Typography>
        {canEdit && (
          <Box>
            <IconButton
              size="small"
              aria-label="editar gráfico"
              onClick={() => onEdit(graph)}
              sx={{
                color: "var(--text-secondary)",
                "&:hover": {
                  color: "var(--primary)",
                  backgroundColor: "var(--tertiary)",
                },
              }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Box>
        )}
      </Box>

      <Box
        sx={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          overflow:
            graph.graphType === "CALENDAR_HEATMAP" || graph.graphType === "MAP"
              ? "visible"
              : "hidden",
        }}
      >
        <Box
          sx={{
            width: "100%",
            height: graph.graphType === "CALENDAR_HEATMAP" ? "auto" : getChartHeight(),
          }}
        >
          {renderGraph()}
        </Box>
      </Box>
    </Paper>
  );
};
